import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { UserOutlined, CameraOutlined, LoadingOutlined, EditOutlined } from "@ant-design/icons";
import { useAuth } from "../contexts/AuthContext";
import { userService } from "../services/user.service";
import { colors } from "../styles";
import { getDefaultProfileImage } from "../config/constants";
import { useToast } from '../contexts/ToastContext';
import Input from './inputs/StyledInput';
import TextArea from './inputs/StyledTextArea';
import ImageUpload from './upload/ImageUpload';

const PageContainer = styled.div`
  background-color: ${colors.primary};
  min-height: calc(100vh - 80px);
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const Profile = () => {
  const { user, userProfile, updateProfile } = useAuth();
  const toast = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [formData, setFormData] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user && userProfile) {
      setFormData({
        displayName: userProfile?.displayName || "",
        username: userProfile?.username || "",
        bio: userProfile?.bio || "",
        photoURL: userProfile?.photoURL || user?.photoURL || getDefaultProfileImage(user.email),
      });
      setLoading(false);
    } else if (!loading && user && !userProfile) {
      setError('Error al cargar el perfil');
      setLoading(false);
    }
  }, [user, userProfile, loading]);

  if (loading || !formData) {
    return (
      <PageContainer>
        <Container>
          <LoadingContainer>
            <LoadingOutlined style={{ fontSize: 24 }} spin />
            <p>Cargando perfil...</p>
          </LoadingContainer>
        </Container>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Container>
          <ErrorContainer>
            <p>{error}</p>
            <RetryButton onClick={() => window.location.reload()}>
              Reintentar
            </RetryButton>
          </ErrorContainer>
        </Container>
      </PageContainer>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.displayName || !formData.username) return;
    
    setLoading(true);
    try {
      await updateProfile(formData);
      toast.showToast("Perfil actualizado correctamente", 'success');
      setIsEditing(false);
    } catch (error) {
      console.error('Error:', error);
      toast.showToast("Error al actualizar el perfil", 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadChange = async (info) => {
    if (info.status === "done") {
      try {
        const url = URL.createObjectURL(info.file);
        setPreviewImage(url);
        
        const photoURL = await userService.uploadProfilePhoto(
          user.uid,
          info.file
        );
        
        setFormData(prev => ({ ...prev, photoURL }));
        setPreviewImage(null);
        toast.showToast("Foto actualizada correctamente", 'success');
      } catch (error) {
        toast.showToast("Error al subir la imagen", 'error');
        setPreviewImage(null);
      } finally {
        setUploadLoading(false);
      }
    }
  };

  return (
    <PageContainer>
      <Container>
        <ProfileCard>
          <PhotoSection>
            <ProfilePhoto
              src={previewImage || formData.photoURL || getDefaultProfileImage(user?.email)}
              alt={formData.displayName || 'Profile'}
              onError={(e) => {
                e.target.src = getDefaultProfileImage(user?.email);
              }}
            />
            {isEditing && (
              <ImageUpload
                onChange={handleUploadChange}
                beforeUpload={(file) => {
                  const isImage = file.type.startsWith("image/");
                  const isLt2M = file.size / 1024 / 1024 < 2;
                  
                  if (!isImage) {
                    toast.showToast("Solo puedes subir imágenes", 'error');
                    return false;
                  }
                  if (!isLt2M) {
                    toast.showToast('La imagen debe ser menor a 2MB', 'error');
                    return false;
                  }
                  return true;
                }}
              >
                <UploadButton>
                  {uploadLoading ? <LoadingOutlined /> : <CameraOutlined />}
                  <span>Cambiar foto</span>
                </UploadButton>
              </ImageUpload>
            )}
          </PhotoSection>

          {isEditing ? (
            <Form onSubmit={handleSubmit}>
              <FormSection>
                <FormGrid>
                  <FormGroup>
                    <Label>Nombre completo *</Label>
                    <Input
                      name="displayName"
                      value={formData.displayName}
                      onChange={handleChange}
                      icon={<UserOutlined />}
                      placeholder="Tu nombre completo"
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Nombre de usuario *</Label>
                    <Input
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      icon="@"
                      placeholder="username"
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Email</Label>
                    <Input
                      value={user?.email}
                      icon={<MailIcon />}
                      disabled
                    />
                  </FormGroup>

                  <FormGroup className="bio-group">
                    <Label>Bio</Label>
                    <TextArea
                      name="bio"
                      value={formData.bio}
                      onChange={handleChange}
                      placeholder="Cuéntanos sobre ti..."
                      rows={4}
                    />
                  </FormGroup>
                </FormGrid>
                
                <ButtonGroup>
                  <CancelButton type="button" onClick={() => setIsEditing(false)}>
                    Cancelar
                  </CancelButton>
                  <SaveButton 
                    type="submit" 
                    disabled={loading || !formData.displayName || !formData.username}
                  >
                    {loading ? "Guardando..." : "Guardar cambios"}
                  </SaveButton>
                </ButtonGroup>
              </FormSection>
            </Form>
          ) : (
            <InfoSection>
              <InfoHeader>
                <UserInfo>
                  <DisplayName>{formData.displayName}</DisplayName>
                  <Username>@{formData.username}</Username>
                  <Email>
                    <MailIcon /> {user?.email}
                  </Email>
                </UserInfo>
                <EditButton onClick={() => setIsEditing(true)}>
                  <EditOutlined /> Editar perfil
                </EditButton>
              </InfoHeader>
              {formData.bio && <Bio>{formData.bio}</Bio>}
            </InfoSection>
          )}
        </ProfileCard>
      </Container>
    </PageContainer>
  );
};

const ProfileCard = styled.div`
  background: ${colors.white};
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const PhotoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 2rem;
  position: relative;
`;

const ProfilePhoto = styled.img`
  width: 150px;
  height: 150px;
  border-radius: 75px;
  object-fit: cover;
  margin-bottom: 1rem;
  border: 4px solid ${colors.secondary};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
`;

const InfoSection = styled.div`
  padding: 1rem;
`;

const InfoHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  gap: 2rem;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
`;

const DisplayName = styled.h1`
  font-size: 2rem;
  color: ${colors.text};
  margin: 0;
  font-weight: 600;
`;

const Username = styled.h2`
  font-size: 1.2rem;
  color: ${colors.accent};
  margin: 0.5rem 0;
  font-weight: 500;
`;

const Bio = styled.p`
  font-size: 1.1rem;
  color: ${colors.text}dd;
  line-height: 1.6;
  margin: 1rem 0;
`;

const EditButton = styled.button`
  background: transparent;
  border: 2px solid ${colors.secondary};
  color: ${colors.secondary};
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;

  &:hover {
    background: ${colors.secondary}15;
    transform: translateY(-1px);
  }
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: ${colors.text};
`;

const SaveButton = styled.button`
  height: 48px;
  background: ${colors.secondary};
  color: #FFFFFF;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1.1rem;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0 2.5rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  &:hover:not(:disabled) {
    background: ${colors.accent};
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    background: #8a8a8a;
    cursor: not-allowed;
    opacity: 0.8;
    color: #FFFFFF;
  }
`;

const UploadButton = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${colors.white};
  cursor: pointer;
  padding: 0.8rem 1.5rem;
  border-radius: 25px;
  background: ${colors.secondary};
  transition: all 0.3s ease;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    background: ${colors.accent};
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  .anticon {
    font-size: 1.2rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const CancelButton = styled.button`
  height: 48px;
  background: transparent;
  color: ${colors.text};
  border: 2px solid ${colors.border};
  border-radius: 8px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  padding: 0 2rem;
  transition: all 0.3s ease;

  &:hover {
    background: ${colors.border}15;
    border-color: ${colors.text};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Email = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${colors.text}99;
  font-size: 1rem;
`;

const MailIcon = styled(UserOutlined)`
  color: ${colors.accent};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;

  .bio-group {
    grid-column: 1 / -1;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 400px;
  color: ${colors.text};
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 400px;
  color: ${colors.text};
`;

const RetryButton = styled.button`
  background: ${colors.secondary};
  color: #FFFFFF;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1.1rem;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0.8rem 1.5rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  &:hover {
    background: ${colors.accent};
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`;

export default Profile;
